/**
 * Minimal ISO-BMFF box reading, shared by the pieces that have to edit an MP4 in place.
 *
 * Everything here works on a `Uint8Array` of the whole file and never changes any box's size, which is
 * what lets the callers patch fields without repairing parent offsets. Anything that does not parse
 * cleanly is reported rather than guessed at, so a caller can abandon its edit and hand back the
 * original bytes untouched.
 */

export interface BoxHeader {
  type: string;
  /** Offset of the box payload — past size, type and any 64-bit largesize. */
  contentStart: number;
  /** Offset one past the last byte of the box. */
  end: number;
}

export const readUint32 = (buf: Uint8Array, offset: number): number =>
  ((buf[offset] << 24) >>> 0) + (buf[offset + 1] << 16) + (buf[offset + 2] << 8) + buf[offset + 3];

export const writeUint32 = (buf: Uint8Array, offset: number, value: number): void => {
  buf[offset] = (value >>> 24) & 0xff;
  buf[offset + 1] = (value >>> 16) & 0xff;
  buf[offset + 2] = (value >>> 8) & 0xff;
  buf[offset + 3] = value & 0xff;
};

/**
 * Read a 64-bit big-endian value as a JS number.
 *
 * Safe for anything a clip can contain: media timestamps stay far below 2^53 even at a 90 kHz
 * timescale, so the precision loss that makes this generally unsafe cannot be reached here.
 */
export const readUint64 = (buf: Uint8Array, offset: number): number =>
  readUint32(buf, offset) * 0x100000000 + readUint32(buf, offset + 4);

export const writeUint64 = (buf: Uint8Array, offset: number, value: number): void => {
  writeUint32(buf, offset, Math.floor(value / 0x100000000));
  writeUint32(buf, offset + 4, value >>> 0);
};

/**
 * Read one box header at `offset`, or null when the bytes there cannot be one.
 *
 * A `size` of 0 means "runs to the end of the file", which is legal and which we honour rather than
 * bail on — the last `mdat` of a progressively written file is often shaped that way.
 */
export const readBoxHeader = (buf: Uint8Array, offset: number, limit: number): BoxHeader | null => {
  if (offset + 8 > limit) return null;

  let size = readUint32(buf, offset);
  let contentStart = offset + 8;
  const type = String.fromCharCode(buf[offset + 4], buf[offset + 5], buf[offset + 6], buf[offset + 7]);

  if (size === 1) {
    // 64-bit largesize. The high word is beyond anything we will ever see, so only the low word matters.
    if (offset + 16 > limit) return null;
    if (readUint32(buf, offset + 8) !== 0) return null;
    size = readUint32(buf, offset + 12);
    contentStart = offset + 16;
  } else if (size === 0) {
    size = limit - offset;
  }

  const end = offset + size;
  if (size < contentStart - offset || end > limit) return null;
  return { type, contentStart, end };
};

/**
 * Walk the boxes between `start` and `end`, calling `visit` on each one and descending into whichever
 * of them `containers` names. Returns false as soon as the tree stops parsing cleanly, so the caller
 * can abandon its edit instead of writing into arbitrary offsets.
 */
export const walkBoxes = (
  buf: Uint8Array,
  start: number,
  end: number,
  containers: ReadonlySet<string>,
  visit: (box: BoxHeader) => void,
): boolean => {
  let pos = start;
  while (pos < end) {
    const box = readBoxHeader(buf, pos, end);
    if (!box) return false;
    visit(box);
    if (containers.has(box.type) && !walkBoxes(buf, box.contentStart, box.end, containers, visit)) return false;
    if (box.end <= pos) return false; // zero-length box: would loop forever
    pos = box.end;
  }
  return true;
};
